import { ShieldAlert } from 'lucide-react';
import { isApiError } from '@/lib/api/client';
import { t, tp } from '@/lib/i18n';
import { Callout } from '@/ui';
import s from '../Admin.module.css';
import { escalationOf } from '../errors';

/**
 * A refused or failed write, explained. The no-escalation guard gets its own message with the
 * permissions the user lacks; other refusals show the server's sentence as it is.
 */
export function ActionError({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const esc = escalationOf(error);
  if (esc) {
    return (
      <Callout tone="danger" icon={ShieldAlert} title={t('You cannot give more access than you hold yourself')} className={className}>
        <p>
          {t('Nobody can grant, change or remove access beyond their own.')} <q className={s.quote}>{esc.sentence}</q>
        </p>
        <p>{t('You do not hold these permissions yourself:')}</p>
        <ul className={s.missing}>
          {esc.missing.map((m) => (
            <li key={m}>
              <code>{m}</code>
            </li>
          ))}
          {esc.more > 0 && <li>{tp(esc.more, 'and {count} more', 'and {count} more')}</li>}
        </ul>
        <p>{t('Ask someone who holds these permissions, or choose a smaller role or scope.')}</p>
      </Callout>
    );
  }
  const message = error instanceof Error ? error.message : String(error);
  const title = isApiError(error, 403)
    ? t('Not allowed')
    : isApiError(error, 404)
      ? t('Not found')
      : isApiError(error, 409)
        ? t('This conflicts with the current state')
        : isApiError(error, 400) || isApiError(error, 422)
          ? t('Please check what you entered')
          : t('Not saved');
  return (
    <Callout tone="danger" title={title} className={className}>
      {message}
    </Callout>
  );
}
